import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Col, Form, Input, InputNumber, Row, Space, Table, Typography} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    ScanOutlined,
    TeamOutlined,
    TrophyOutlined,
    UserOutlined
} from '@ant-design/icons';
import KioskDebugInfo from "../components/debug/KioskDebugInfo";
import AvatarDisplay from "../components/AvatarDisplay";

const { Title, Text, Paragraph } = Typography;

const KIOSK_STATES = {
    WAITING_FOR_NFC: 'waiting_for_nfc',
    PLAYER_LOADED: 'player_loaded',
    ENTERING_STROKES: 'entering_strokes',
    SUBMITTING: 'submitting'
};

const KioskTerminal = ({
                           players = [],
                           holes = [],
                           api,
                           queryParams = {},
                           showToast,
                           showSuccess,
                           showError,
                           showWarning,
                           showInfo
                       }) => {

    const fixedHoleNumber = queryParams.hole && !isNaN(queryParams.hole);
    const initialHole = fixedHoleNumber ? parseInt(queryParams.hole) : 1;

    const [kioskState, setKioskState] = useState(KIOSK_STATES.WAITING_FOR_NFC);
    const [nfcTagId, setNfcTagId] = useState('');
    const [selectedHole, setSelectedHole] = useState(initialHole);
    const [currentPlayer, setCurrentPlayer] = useState(null);
    const [playerScores, setPlayerScores] = useState({});
    const [holeData, setHoleData] = useState(null);
    const [loading, setLoading] = useState(false);

    const nfcInputRef = useRef(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (queryParams.hole && !isNaN(queryParams.hole)) {
            const holeNumber = parseInt(queryParams.hole);
            if (holeNumber !== selectedHole) {
                setSelectedHole(holeNumber);
            }
        }
    }, [queryParams.hole, selectedHole, setSelectedHole]);

    useEffect(() => {
        const loadHoleData = async () => {
            if (!api || typeof showError !== 'function') return;

            try {
                const hole = await api.getHole(selectedHole);
                setHoleData(hole);
            } catch (error) {
                console.error('Błąd ładowania dołka:', error);
                showError(`Błąd ładowania danych dołka #${selectedHole} ❌`);
            }
        };

        if (selectedHole && api) {
            loadHoleData();
        }
    }, [selectedHole, api, showError]);

    useEffect(() => {
        const setFocusWithRetry = (element, retries = 3) => {
            if (!element || retries <= 0) return;
            element.focus();
            setTimeout(() => {
                if (document.activeElement !== element) {
                    console.log(`🔄 Retry focus (${retries} left)`);
                    setFocusWithRetry(element, retries - 1);
                }
            }, 100);
        };

        switch (kioskState) {
            case KIOSK_STATES.WAITING_FOR_NFC:
                if (nfcInputRef.current) {
                    console.log('🎯 Focus: NFC input');
                    setFocusWithRetry(nfcInputRef.current);
                }
                break;
            case KIOSK_STATES.ENTERING_STROKES:
                // Focus na pierwszy input wyników
                setTimeout(() => {
                    const firstInput = document.querySelector('.ant-input-number-input');
                    if (firstInput) {
                        console.log('🎯 Focus: First score input');
                        firstInput.focus();
                    }
                }, 100);
                break;
            default:
                if (document.activeElement && document.activeElement.blur) {
                    document.activeElement.blur();
                }
                break;
        }
    }, [kioskState]);

    useEffect(() => {
        if (kioskState === KIOSK_STATES.WAITING_FOR_NFC) {
            const interval = setInterval(() => {
                if (nfcInputRef.current && document.activeElement !== nfcInputRef.current) {
                    console.log('🔧 Re-focusing NFC input');
                    nfcInputRef.current.focus();
                }
            }, 1000);

            const handleGlobalClick = () => {
                if (kioskState === KIOSK_STATES.WAITING_FOR_NFC && nfcInputRef.current) {
                    setTimeout(() => {
                        nfcInputRef.current.focus();
                    }, 10);
                }
            };

            document.addEventListener('click', handleGlobalClick);

            return () => {
                clearInterval(interval);
                document.removeEventListener('click', handleGlobalClick);
            };
        }
    }, [kioskState]);

    const handleNfcScan = async () => {
        if (!api || typeof showWarning !== 'function') return;

        if (!nfcTagId.trim()) {
            showWarning('Proszę wprowadzić ID tagu NFC 📱');
            return;
        }

        setLoading(true);
        setKioskState(KIOSK_STATES.SUBMITTING);

        try {
            const player = await api.getPlayer(nfcTagId);
            setCurrentPlayer(player);

            const defaultStrokes = holeData?.par || 3;
            const initialScores = {};
            player.playerNames.forEach(name => {
                initialScores[name] = defaultStrokes;
            });
            setPlayerScores(initialScores);
            form.setFieldsValue(initialScores);

            setKioskState(KIOSK_STATES.PLAYER_LOADED);

            if (typeof showSuccess === 'function') {
                const groupName = player.groupName || 'Grupa';
                showSuccess(`Witaj grupa "${groupName}"! 🎉`);
            }

            setTimeout(() => {
                setKioskState(KIOSK_STATES.ENTERING_STROKES);
            }, 1000);

        } catch (error) {
            if (typeof showError === 'function') {
                showError(`Błąd skanowania NFC: ${error.message} ❌`);
            }
            resetKioskState();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleNfcInput = async () => {
            if (kioskState === KIOSK_STATES.WAITING_FOR_NFC && nfcTagId.trim().length >= 3) {
                console.log('📱 Auto-skanowanie NFC:', nfcTagId);
                await handleNfcScan();
            }
        };

        const timeoutId = setTimeout(handleNfcInput, 200);
        return () => clearTimeout(timeoutId);
    });

    const handleScoreSubmit = async () => {
        if (!api || typeof showError !== 'function') return;

        if (!currentPlayer) {
            showError('Najpierw zeskanuj tag NFC! 📱');
            return;
        }

        // Walidacja wyników - sprawdź wszystkich graczy
        const allPlayerNames = currentPlayer.playerNames;
        const missingScores = allPlayerNames.filter(name => !playerScores[name] || playerScores[name] < 1);

        if (missingScores.length > 0) {
            if (typeof showWarning === 'function') {
                showWarning(`Proszę wprowadzić wyniki dla wszystkich graczy: ${missingScores.join(', ')} ⛳`);
            }
            return;
        }

        // Stwórz listę wyników dla wszystkich graczy
        const playerScoresList = allPlayerNames.map(playerName => ({
            playerName,
            strokes: parseInt(playerScores[playerName])
        }));

        // Sprawdź, czy wszystkie wyniki są prawidłowe
        const invalidScores = playerScoresList.filter(p => !p.strokes || p.strokes < 1 || p.strokes > 20);
        if (invalidScores.length > 0) {
            if (typeof showWarning === 'function') {
                showWarning('Wszystkie wyniki muszą być liczbą między 1 a 20 ⛳');
            }
            return;
        }

        setLoading(true);
        setKioskState(KIOSK_STATES.SUBMITTING);

        try {
            console.log('Submitting group scores...', {
                nfcTagId: currentPlayer.nfcTagId,
                holeNumber: selectedHole,
                playerScores: playerScoresList
            });

            const result = await api.submitGroupScore({
                nfcTagId: currentPlayer.nfcTagId,
                holeNumber: selectedHole,
                playerScores: playerScoresList
            });

            const totalPoints = result.totalPoints || result.score?.totalPoints || 0;
            const totalStrokes = playerScoresList.reduce((sum, p) => sum + p.strokes, 0);

            if (typeof showSuccess === 'function') {
                showSuccess(`Wynik grupy zapisany! ${totalStrokes} ruchów = ${totalPoints} punktów 🏆`);
            }

            resetKioskState();

            setTimeout(() => {
                if (typeof showInfo === 'function') {
                    showInfo('Gotowy na następną grupę! Przyłóż tag NFC 📱');
                }
            }, 2000);

            try {
                const updatedHole = await api.getHole(selectedHole);
                setHoleData(updatedHole);
            } catch (holeError) {
                console.error('Błąd odświeżania danych dołka:', holeError);
            }

        } catch (error) {
            if (typeof showError === 'function') {
                showError(`Błąd zapisywania wyniku: ${error.message} ❌`);
            }
            setKioskState(KIOSK_STATES.ENTERING_STROKES);
        } finally {
            setLoading(false);
        }
    };

    const resetKioskState = () => {
        setPlayerScores({});
        setCurrentPlayer(null);
        setNfcTagId('');
        form.resetFields();
        setKioskState(KIOSK_STATES.WAITING_FOR_NFC);
    };

    const handleKeyPress = (e, action) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            action();
        }
    };

    const forceFocusNFC = () => {
        if (nfcInputRef.current) {
            nfcInputRef.current.focus();
            nfcInputRef.current.select();
            console.log('🔧 Forced NFC focus');
        }
    };

    const handleCancel = () => {
        resetKioskState();
        if (typeof showInfo === 'function') {
            showInfo('Anulowano wprowadzanie wyniku 🚫');
        }
    };

    const handleScoreChange = (playerName, value) => {
        setPlayerScores(prev => ({
            ...prev,
            [playerName]: value
        }));

        if (value && !isNaN(value) && typeof showInfo === 'function' && holeData?.par) {
            const strokesNum = parseInt(value);
            const par = holeData.par;
            if (strokesNum === 1) {
                showInfo(`${playerName}: Hole in One! 🎯`);
            } else if (strokesNum <= par - 2) {
                showInfo(`${playerName}: Eagle! 🦅`);
            } else if (strokesNum === par - 1) {
                showInfo(`${playerName}: Birdie! 🐦`);
            } else if (strokesNum === par) {
                showInfo(`${playerName}: Par 👍`);
            }
        }
    };

    const isFormComplete = currentPlayer &&
        currentPlayer.playerNames &&
        currentPlayer.playerNames.length > 0 &&
        currentPlayer.playerNames.every(name => playerScores[name] && playerScores[name] >= 1);

    const scoresColumns = [
        {
            title: 'Grupa',
            dataIndex: 'groupName',
            key: 'group',
            render: (text, record) => (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <AvatarDisplay
                        avatarId={record.avatarId || 1}
                        size={28}
                        showBorder={false}
                    />
                    <div>
                        <Text style={{color: 'var(--color-white)', display: 'block'}}>
                            "{record.groupName || 'Nieznana grupa'}"
                        </Text>
                        <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                            {record.playerName}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: 'Ruchy',
            dataIndex: 'strokes',
            key: 'strokes',
            render: (value) => <Text style={{color: 'var(--color-white)'}}>{value || 0}</Text>,
        },
        {
            title: 'Punkty',
            dataIndex: 'points',
            key: 'points',
            render: (value) => <Text style={{color: 'var(--color-white)'}}>{value || 0}</Text>,
        },
        {
            title: 'Czas',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (value) => (
                <Text style={{color: 'var(--color-white)', fontSize: '12px'}}>
                    {value ? new Date(value).toLocaleTimeString('pl-PL') : '-'}
                </Text>
            ),
        },
    ];

    if (!api || typeof showToast !== 'function') {
        return (
            <div className="container">
                <div className="overlay-gradient"></div>
                <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>
                    <div className="grid-span-8">
                        <Card className="card center" style={{minHeight: '60vh'}}>
                            <Title level={2} className="tablet" style={{color: 'var(--color-white)', textAlign: 'center'}}>
                                ⛳ Kiosk - Terminal
                            </Title>
                            <div style={{textAlign: 'center'}}>
                                <LoadingOutlined style={{fontSize: 48, color: 'var(--color-white)', marginBottom: 16}} spin />
                                <Paragraph style={{color: 'var(--color-white)', fontSize: 18}}>
                                    ⏳ Ładowanie danych terminala...
                                </Paragraph>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container" onClick={forceFocusNFC}>
            <div className="overlay-gradient"></div>
            <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>
                <div className="grid-span-8">
                    {/* Header */}
                    <div style={{textAlign: 'center', marginBottom: 32}}>
                        <Title level={1} className="tablet" style={{color: 'var(--color-white)', margin: 0}}>
                            ⛳ Kiosk - Dołek #{selectedHole}
                        </Title>
                        {fixedHoleNumber && holeData && (
                            <Title level={3} style={{color: 'var(--white-60)', margin: '8px 0 0 0'}}>
                                Par: {holeData.par}
                            </Title>
                        )}
                    </div>

                    {/* NFC Section */}
                    <Card className="card" style={{marginBottom: 24}}>
                        {kioskState === KIOSK_STATES.WAITING_FOR_NFC && (
                            <Alert
                                message="Gotowy do skanowania"
                                description={
                                    <div>
                                        <p>💡 Przyłóż tag NFC do czytnika</p>
                                        <p>🔄 System automatycznie rozpozna grupę</p>
                                    </div>
                                }
                                type="info"
                                icon={<InfoCircleOutlined />}
                                style={{
                                    backgroundColor: 'var(--white-10)',
                                    border: '1px solid var(--white-30)',
                                    marginBottom: 16
                                }}
                            />
                        )}

                        <Input
                            ref={nfcInputRef}
                            placeholder="NFC Tag ID zostanie wczytany automatycznie..."
                            value={nfcTagId}
                            onChange={(e) => setNfcTagId(e.target.value)}
                            onKeyPress={(e) => handleKeyPress(e, handleNfcScan)}
                            disabled={loading}
                            size="large"
                            prefix={<ScanOutlined />}
                            className="input"
                            style={{
                                opacity: kioskState === KIOSK_STATES.WAITING_FOR_NFC ? 1 : 0.3,
                                height: kioskState === KIOSK_STATES.WAITING_FOR_NFC ? '50px' : '30px',
                                fontSize: kioskState === KIOSK_STATES.WAITING_FOR_NFC ? '16px' : '12px',
                                border: kioskState === KIOSK_STATES.WAITING_FOR_NFC ?
                                    '2px dashed var(--color-light-green)' : '1px solid var(--white-30)',
                                transition: 'all 0.3s ease',
                                position: kioskState === KIOSK_STATES.WAITING_FOR_NFC ? 'relative' : 'absolute',
                                top: kioskState === KIOSK_STATES.WAITING_FOR_NFC ? 'auto' : '-1000px',
                            }}
                            autoComplete="off"
                            spellCheck="false"
                        />
                    </Card>

                    {/* Player Info */}
                    {(kioskState === KIOSK_STATES.PLAYER_LOADED || kioskState === KIOSK_STATES.ENTERING_STROKES) && currentPlayer && (
                        <Card className="card" style={{marginBottom: 24}}>
                            <div style={{textAlign: 'center'}}>
                                <div style={{marginBottom: 16}}>
                                    <AvatarDisplay
                                        avatarId={
                                            currentPlayer.avatar?.numericId ||
                                            currentPlayer.avatarId ||
                                            1
                                        }
                                        size={64}
                                        showBorder={true}
                                    />
                                </div>

                                <Title level={3} style={{color: 'var(--color-white)', margin: '0 0 8px 0'}}>
                                    🏌️ Grupa: "{currentPlayer.groupName || 'Nieznana'}"
                                </Title>

                                {currentPlayer.playerNames && currentPlayer.playerNames.length > 0 && (
                                    <Text style={{color: 'var(--white-60)', fontSize: 14, display: 'block', marginBottom: 16}}>
                                        Gracze: {currentPlayer.playerNames.join(', ')} ({currentPlayer.playerNames.length} osób)
                                    </Text>
                                )}

                                <Space direction="vertical" size="small">
                                    <Text style={{color: 'var(--white-80)', fontSize: 16}}>
                                        Łączny wynik: <Text strong style={{color: 'var(--color-white)'}}>{currentPlayer.totalScore || 0} punktów</Text>
                                    </Text>
                                    <Text style={{color: 'var(--white-80)', fontSize: 16}}>
                                        Rozegrane dołki: <Text strong style={{color: 'var(--color-white)'}}>{currentPlayer.holes?.length || 0}/18</Text>
                                    </Text>
                                </Space>
                            </div>
                        </Card>
                    )}

                    {/* Score Input */}
                    {kioskState === KIOSK_STATES.ENTERING_STROKES && currentPlayer && (
                        <Card className="card" style={{marginBottom: 24}}>
                            <Title level={4} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 24}}>
                                <TeamOutlined style={{marginRight: 8}} />
                                Wprowadź wyniki dla dołka #{selectedHole} (Par {holeData?.par || '?'})
                            </Title>

                            <Form form={form} layout="vertical" onFinish={handleScoreSubmit}>
                                <Row gutter={[16, 16]}>
                                    {currentPlayer.playerNames.map((playerName, index) => (
                                        <Col xs={24} sm={12} md={8} key={playerName}>
                                            <div style={{
                                                background: 'var(--white-10)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '16px',
                                                border: '1px solid var(--white-30)',
                                                textAlign: 'center'
                                            }}>
                                                <UserOutlined style={{
                                                    fontSize: '24px',
                                                    color: 'var(--color-white)',
                                                    marginBottom: '8px'
                                                }} />
                                                <Text style={{
                                                    color: 'var(--color-white)',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '12px'
                                                }}>
                                                    {playerName}
                                                </Text>
                                                <Form.Item
                                                    name={playerName}
                                                    rules={[
                                                        { required: true, message: 'Wprowadź liczbę ruchów' },
                                                        { type: 'number', min: 1, max: 20, message: 'Od 1 do 20 ruchów' }
                                                    ]}
                                                    style={{marginBottom: 0}}
                                                >
                                                    <InputNumber
                                                        min={1}
                                                        max={20}
                                                        size="large"
                                                        placeholder="Ruchy"
                                                        style={{
                                                            width: '100%',
                                                            textAlign: 'center',
                                                            fontSize: '18px'
                                                        }}
                                                        disabled={loading}
                                                        onChange={(value) => handleScoreChange(playerName, value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                // Focus na następny input lub submit
                                                                if (index < currentPlayer.playerNames.length - 1) {
                                                                    const nextInput = document.querySelector(`input[id$="${currentPlayer.playerNames[index + 1]}"]`);
                                                                    if (nextInput) nextInput.focus();
                                                                } else {
                                                                    handleScoreSubmit();
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>

                                <div style={{textAlign: 'center', marginTop: '24px'}}>
                                    <Space size="middle">
                                        <Button
                                            type="primary"
                                            size="large"
                                            htmlType="submit"
                                            disabled={loading || !isFormComplete}
                                            loading={loading}
                                            icon={<CheckOutlined />}
                                            className="button primary large"
                                        >
                                            {loading ? 'Zapisywanie...' : 'Zapisz wyniki grupy'}
                                        </Button>
                                        <Button
                                            size="large"
                                            onClick={handleCancel}
                                            disabled={loading}
                                            icon={<CloseOutlined />}
                                            className="button secondary large"
                                        >
                                            Anuluj
                                        </Button>
                                    </Space>
                                </div>
                            </Form>
                        </Card>
                    )}

                    {/* Hole Scores */}
                    {holeData && (
                        <Card className="card">
                            <Title level={3} style={{color: 'var(--color-white)', marginBottom: 24}}>
                                <TrophyOutlined style={{marginRight: 8}} />
                                📊 Wyniki dla dołka #{selectedHole}
                            </Title>

                            {!holeData.scores || holeData.scores.length === 0 ? (
                                <div style={{textAlign: 'center', padding: '40px 0'}}>
                                    <InfoCircleOutlined style={{fontSize: 48, color: 'var(--white-40)', marginBottom: 16}} />
                                    <Text style={{color: 'var(--white-60)', fontSize: 16}}>
                                        Brak wyników dla tego dołka
                                    </Text>
                                </div>
                            ) : (
                                <Table
                                    columns={scoresColumns}
                                    dataSource={holeData.scores.map((score, index) => ({
                                        ...score,
                                        key: index
                                    }))}
                                    pagination={false}
                                    size="middle"
                                    style={{
                                        '--ant-table-bg': 'transparent',
                                        '--ant-table-header-bg': 'var(--white-10)',
                                    }}
                                />
                            )}
                        </Card>
                    )}

                    {/* Debug Info */}
                    <div style={{marginTop: 24}}>
                        <KioskDebugInfo
                            kioskState={kioskState}
                            nfcTagId={nfcTagId}
                            KIOSK_STATES={KIOSK_STATES}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KioskTerminal;